import { useState } from "react";
import { download, importStudent, upload } from "../../asset";
import DocumentUpload from "../../components/common/DocumetUpload";
import CustomButton from "../../components/CustomButton";
import PageHeader from "../../components/PageHeader";
import { exportToExcel } from "../../util/functions/UniversalFunctions";
import { useBranchCode } from "../../util/hooks/useBranch";
import * as XLSX from "xlsx";
import StudentApis from "../../api/studentApis";
import { toast } from "sonner";
import { AddStudentForm } from "../../types/studentType";

const STUDENT_TEMPLATE = [
  {
    admissionNumber: "",
    fullName: "",
    gender: "",
    dateOfBirth: "",
    admissionYear: "",
    academicCycleId: "",
    academicCycle: "",
    motherFullName: "",
    motherPhoneNumber: "",
    motherEmail: "",
    fatherFullName: "",
    fatherPhoneNumber: "",
    fatherEmail: "",
    studentCategory: "",
  },
];

export default function StudentDataImport() {
  const [file, setFile] = useState<File | null>(null);
  const branchCode = useBranchCode();

  const handleDownloadTemplate = () => {
    exportToExcel(STUDENT_TEMPLATE, "Student_Import_Template");
  };

  // Convert Excel date number → YYYY-MM-DD
  const excelDateToJSDate = (serial: number) => {
    const utcDays = Math.floor(serial - 25569);
    const utcValue = utcDays * 86400;
    const dateInfo = new Date(utcValue * 1000);
    return dateInfo.toISOString().split("T")[0];
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select an Excel file first");
      return;
    }

    try {
      const data = await file.arrayBuffer();

      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const students = XLSX.utils.sheet_to_json<AddStudentForm>(sheet);

      if (!students.length) {
        toast.error("Excel file is empty");
        return;
      }

      const payloads = students
        .map((row, index) => {
          // basic validation
          if (!row.fullName || !row.gender || !row.dateOfBirth) {
            console.warn(`Row ${index + 2} skipped (missing required fields)`);
            return null;
          }

          return {
            admissionNumber: row.admissionNumber || "",
            fullName: row.fullName,

            gender: row.gender?.toUpperCase(),

            dateOfBirth:
              typeof row.dateOfBirth === "number"
                ? excelDateToJSDate(row.dateOfBirth)
                : row.dateOfBirth,

            admissionYear: Number(row.admissionYear),

            academicCycleId: Number(row.academicCycleId),


            academicCycle: row.academicCycle || "",

            motherFullName: row.motherFullName || "",
            motherPhoneNumber: row.motherPhoneNumber || "",
            motherEmail: row.motherEmail || "",

            fatherFullName: row.fatherFullName || "",
            fatherPhoneNumber: row.fatherPhoneNumber || "",
            fatherEmail: row.fatherEmail || "",

            branchCode: branchCode,

            classId: row.classId ? Number(row.classId) : null,
            groupId: row.groupId ? Number(row.groupId) : null,

            studentCategory: row.studentCategory || "",
          };
        })
        .filter(Boolean);

      if (!payloads.length) {
        toast.error("No valid student records found in file");
        return;
      }

      // Upload students in parallel
      const results = await Promise.allSettled(
        payloads.map((payload) => StudentApis.addStudent(payload))
      );

      const successCount = results.filter(
        (r) => r.status === "fulfilled"
      ).length;

      const failedCount = results.filter(
        (r) => r.status === "rejected"
      ).length;

      if (successCount) {
        toast.success(`${successCount} students imported successfully`);
      }

      if (failedCount) {
        toast.error(`${failedCount} students failed to import`);
      }

      setFile(null);
    } catch (error) {
      console.error(error);
      toast.error("Import failed. Please check the Excel format.");
    }
  };

  return (
    <div className="grid gap-4">
      <PageHeader showBack />

      <div className="gap-14 bg-white px-6 py-7 rounded-lg grid place-content-center">
        <div className="flex flex-col gap-3 justify-center items-center text-center">
          <span className="text-neutral-300 text-sm font-semibold">
            How it works?
          </span>

          <h3 className="text-primary-600 text-[28px] font-semibold">
            Upload bulk student data in just 3 easy steps
          </h3>
        </div>

        <div className="flex flex-col lg:flex-row justify-between items-center gap-10 w-full">
          <div className="bg-success-50 p-10 lg:p-16 rounded-lg w-full lg:max-w-96 flex items-center justify-center h-full">
            <img
              src={importStudent}
              alt="Upload bulk student data in just 3 easy steps"
              className="w-full h-auto max-h-[400px] object-contain"
            />
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-10 w-full lg:h-full justify-between">
            
            {/* STEP 1 */}
            <div className="flex gap-6 items-start flex-wrap sm:flex-nowrap">
              <div className="bg-neutral-50 rounded px-3 p-1 flex flex-col items-center text-sm text-neutral-300 font-medium">
                <span>STEP</span>
                <span>1</span>
              </div>

              <div className="text-neutral-300 flex flex-col gap-4">
                <div>
                  <p className="text-xl font-semibold">
                    Download Excel Template
                  </p>

                  <p className="text-sm">
                    Start by downloading the Excel template to easily enter all
                    student information in one place.
                  </p>
                </div>

                <CustomButton
                  text="Download Template"
                  color="bg-primary-500"
                  onClick={handleDownloadTemplate}
                  icon={download}
                />
              </div>
            </div>

            {/* STEP 2 */}
            <div className="flex gap-6 items-start flex-wrap sm:flex-nowrap">
              <div className="bg-neutral-50 rounded px-3 p-1 flex flex-col items-center text-sm text-neutral-300 font-medium">
                <span>STEP</span>
                <span>2</span>
              </div>

              <div className="text-neutral-300 flex flex-col gap-4">
                <div>
                  <p className="text-xl font-semibold">Choose Excel File</p>

                  <p className="text-sm">
                    Next, upload the Excel sheet you’ve filled out with the
                    student details to proceed.
                  </p>
                </div>

                <DocumentUpload
                  onFileSelect={(file) => setFile(file)}
                  showDropzone={false}
                  buttonText="Choose File"
                  buttonColor="bg-primary-500"
                  accept=".xls,.xlsx"
                  buttonWidth="fit"
                  className="flex justify-start items-start"
                />
              </div>
            </div>

            {/* STEP 3 */}
            <div className="flex gap-6 items-start flex-wrap sm:flex-nowrap">
              <div className="bg-neutral-50 rounded px-3 p-1 flex flex-col items-center text-sm text-neutral-300 font-medium">
                <span>STEP</span>
                <span>3</span>
              </div>

              <div className="text-neutral-300 flex flex-col gap-4">
                <div>
                  <p className="text-xl font-semibold">Upload File</p>

                  <p className="text-sm">
                    Sit back while we automatically process the file and
                    generate your complete student database.
                  </p>
                </div>

                <CustomButton
                  text="Upload"
                  color="bg-primary-500"
                  icon={upload}
                  onClick={handleUpload}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
